/**
 * WeeklyReportManager — Admin tab for previewing, approving, and sending weekly portal progress reports
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const WeeklyReportManager = () => {
  const { instance, accounts } = useMsal();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [days, setDays] = useState(7);

  const syncHeaders = useCallback(() => ({ 'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone }), []);

  const fetchDraft = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/weekly-report/draft', null, syncHeaders());
      setDraft(res.data.hasDraft ? res.data : null);
    } catch (err) {
      console.error('Failed to fetch draft:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, syncHeaders]);

  useEffect(() => { fetchDraft(); }, [fetchDraft]);

  const handlePreview = async () => {
    try {
      setActionLoading(true);
      setMessage(null);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/weekly-report/preview', { days }, syncHeaders());
      setMessage({ type: 'success', text: `Preview sent to ${res.data.previewSentTo}. Check your email.` });
      fetchDraft();
    } catch (err) {
      setMessage({ type: 'error', text: `Preview failed: ${err.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm(`Send to all ${draft?.recipientCount || 0} recipients? This cannot be undone.`)) return;
    try {
      setActionLoading(true);
      setMessage(null);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/weekly-report/approve', {}, syncHeaders());
      setMessage({ type: 'success', text: `Sent to ${res.data.recipientCount} recipients.` });
      setDraft(null);
    } catch (err) {
      setMessage({ type: 'error', text: `Send failed: ${err.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDiscard = async () => {
    try {
      setActionLoading(true);
      await authenticatedApiCall(instance, accounts, 'DELETE', '/weekly-report/draft', null, syncHeaders());
      setDraft(null);
      setMessage({ type: 'info', text: 'Draft discarded.' });
    } catch (err) {
      setMessage({ type: 'error', text: `Discard failed: ${err.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' };

  if (loading) {
    return <div style={{ padding: '2rem', color: '#6b7280' }}>Loading weekly report status...</div>;
  }

  return (
    <div style={{ maxWidth: '900px' }}>
      <h3 style={{ margin: '0 0 1rem', color: '#111827' }}>Weekly Progress Report</h3>
      <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '1.5rem' }}>
        Generate a preview email (sent to you only), review it, then approve to send to all PMs and management.
      </p>

      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: '6px', marginBottom: '1rem', fontSize: '13px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : message.type === 'error' ? '#fee2e2' : '#dbeafe',
          color: message.type === 'success' ? '#166534' : message.type === 'error' ? '#991b1b' : '#1e40af',
          border: `1px solid ${message.type === 'success' ? '#bbf7d0' : message.type === 'error' ? '#fecaca' : '#bfdbfe'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Generate Preview */}
      <div style={{ ...cardStyle, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>Generate Preview</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Creates a draft and sends a preview to your email</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#6b7280' }}>Days:</label>
            <input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 7)} min={1} max={30}
              style={{ width: '60px', padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center' }} />
            <button onClick={handlePreview} disabled={actionLoading}
              style={{ padding: '8px 20px', fontSize: '13px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '6px', cursor: actionLoading ? 'wait' : 'pointer', opacity: actionLoading ? 0.6 : 1 }}>
              {actionLoading ? 'Generating...' : 'Generate Preview'}
            </button>
          </div>
        </div>
      </div>

      {/* Draft Status */}
      {draft && (
        <div style={{ ...cardStyle, marginBottom: '1rem', borderLeft: '4px solid #ffc107' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '8px' }}>
                Pending Draft: {draft.subject}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                Prepared: {new Date(draft.preparedAt).toLocaleString()} | Recipients: {draft.recipientCount}
              </div>
              {draft.recipientNames && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.6' }}>
                  {draft.recipientNames.join(', ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleApprove} disabled={actionLoading}
                style={{ padding: '8px 20px', fontSize: '13px', backgroundColor: '#198754', color: '#fff', border: 'none', borderRadius: '6px', cursor: actionLoading ? 'wait' : 'pointer', opacity: actionLoading ? 0.6 : 1, fontWeight: '600' }}>
                Send to All ({draft.recipientCount})
              </button>
              <button onClick={handleDiscard} disabled={actionLoading}
                style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: '#f3f4f6', color: '#dc3545', border: '1px solid #fca5a5', borderRadius: '6px', cursor: actionLoading ? 'wait' : 'pointer' }}>
                Discard
              </button>
            </div>
          </div>

          {draft.htmlPreview && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Email Preview:</div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', maxHeight: '500px', overflowY: 'auto' }}>
                <iframe
                  title="Weekly Report Preview"
                  srcDoc={draft.htmlPreview}
                  style={{ width: '100%', height: '450px', border: 'none' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!draft && (
        <div style={{ ...cardStyle, textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
          No pending draft. Generate a preview to get started.
        </div>
      )}
    </div>
  );
};

export default WeeklyReportManager;
